import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { Button, Form, Row, Col } from "react-bootstrap";
import api, { BASE_URL } from "../../Utils/api";
import { IMaskInput } from "react-imask";
import Modal from "react-bootstrap/Modal";
import { FaKey } from "react-icons/fa";
import ErrorAlert from "../../Utils/ErrorAlert";
import { GlobalContext } from "../../Utils/GlobalContext";
import { Salvar, Cancelar } from "../Generals/buttons";
import { Email } from "../Generals/Inputs";
import { useFeedback } from "../Generals/Feedback/FeedbackContext";
import { normalizarEmail, validarEmail } from "../../Utils/validarEmail";
import { podeAdicionarUsuarioOperacional } from "../../Utils/verificarLimiteDocumentosAtivos";
import GruposUsuarioModal from "../GruposUsuario/GruposUsuarioModal";
import "./UsuariosModal.css";


function extrairMensagemErro(err, fallback = "Erro ao salvar usuário") {
  const data = err?.response?.data;
  if (!data) return fallback;
  if (typeof data.message === "string" && data.message.trim()) return data.message;
  if (typeof data.error === "string" && data.error.trim()) return data.error;
  return fallback;
}


const UsuarioModal = ({ show, handleClose, usuario, fetchUsuarios, usuarioLogadoId = null }) => {
  const { toast } = useFeedback();
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [cpf, setCpf] = useState("");
  const [dataNascimento, setDataNascimento] = useState("");
  const [situacao, setSituacao] = useState("A");
  const [tipo, setTipo] = useState("");
  const [idGrupoUsuario, setIdGrupoUsuario] = useState("");
  const [grupos, setGrupos] = useState([]);
  const [showGrupoUsuarioModal, setShowGrupoUsuarioModal] = useState(false);
  const [error, setError] = useState(null);
  const [executandoVisaoPadrao, setExecutandoVisaoPadrao] = useState(false);
  const [resetandoSenha, setResetandoSenha] = useState(false);
  const idsGruposAntesCadastroRef = useRef(new Set());
  const { globalData } = useContext(GlobalContext);

  const entidadeID = globalData?.entidade_id || localStorage.getItem("entidade_id");
  const token = localStorage.getItem("authToken");

  const ehUsuarioLogado = !!usuarioLogadoId && !!usuario && String(usuario.id) === String(usuarioLogadoId);

  const recarregarGrupos = useCallback(async (idGrupoPreferencial = null, selecionarCriado = false) => {
    if (!entidadeID) return [];
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const res = await api.get(`${BASE_URL}/api/gruposusuario/ListarGruposUsuario/${entidadeID}`, { headers });
      const lista = Array.isArray(res.data?.data) ? res.data.data : [];
      lista.sort((a, b) => (a.descricao || "").localeCompare(b.descricao || ""));
      setGrupos(lista);

      const grupoCriado = selecionarCriado
        ? lista.find((grupo) => !idsGruposAntesCadastroRef.current.has(String(grupo.id)))
        : null;
      const idParaSelecionar = idGrupoPreferencial ?? grupoCriado?.id;

      if (idParaSelecionar != null) {
        setIdGrupoUsuario(String(idParaSelecionar));
      }

      idsGruposAntesCadastroRef.current = new Set(lista.map((grupo) => String(grupo.id)));
      return lista;
    } catch (err) {
      console.error("Erro ao listar grupos:", err);
      return [];
    }
  }, [entidadeID, token]);

  useEffect(() => {
    if (show && entidadeID) {
      recarregarGrupos();
    }
  }, [show, entidadeID, recarregarGrupos]);

  useEffect(() => {
    if (!show) return;
    setError(null);
    if (usuario) {
      setNome(usuario.nome || "");
      setEmail(usuario.email || "");
      setTelefone(usuario.telefone || "");
      setCpf(usuario.cpf || "");
      setSituacao(usuario.situacao || "A");
      setTipo(ehUsuarioLogado ? "A" : (usuario.tipo || ""));
      setDataNascimento(usuario.data_nascimento || "");
      setIdGrupoUsuario(usuario.id_grupo_usuario || "");
    } else {
      setNome("");
      setEmail("");
      setTelefone("");
      setCpf("");
      setSituacao("A");
      setTipo("");
      setDataNascimento("");
      setIdGrupoUsuario("");
    }
  }, [show, usuario, ehUsuarioLogado]);

  useEffect(() => {
    if (show && !usuario && !idGrupoUsuario && grupos.length === 1) {
      setIdGrupoUsuario(String(grupos[0].id));
    }
  }, [show, usuario, idGrupoUsuario, grupos]);

  const abrirCadastroGrupoUsuario = () => {
    idsGruposAntesCadastroRef.current = new Set(grupos.map((grupo) => String(grupo.id)));
    setShowGrupoUsuarioModal(true);
  };

  const handleCloseGrupoUsuarioModal = async (resultado) => {
    setShowGrupoUsuarioModal(false);
    const idCriado = resultado?.id ?? resultado?.id_grupo_usuario;
    await recarregarGrupos(idCriado != null ? String(idCriado) : null, Boolean(resultado));
  };

  const verificarCamposObrigatorios = () => {
    const faltantes = [];
    if (!tipo) faltantes.push("Tipo");
    if (!nome.trim()) faltantes.push("Nome");
    if (!normalizarEmail(email)) faltantes.push("Email");
    else if (!validarEmail(email)) faltantes.push("Email (formato inválido)");
    if (tipo === "N" && !idGrupoUsuario) faltantes.push("Grupo de Usuário");
    return faltantes;
  };

  const handleSave = async () => {
    setError(null);

    const camposFaltantes = verificarCamposObrigatorios();
    if (camposFaltantes.length > 0) {
      setError({
        message: `Os seguintes campos obrigatórios não foram preenchidos: ${camposFaltantes.join(", ")}.`,
      });
      return;
    }

    // Limite de usuários operacionais do plano — só se aplica a usuários NOVOS
    // e que não sejam do tipo Comunicado (esse tipo não conta no limite, é
    // ilimitado). Checado aqui (ao salvar), não mais ao clicar em Adicionar,
    // já que só agora sabemos qual tipo foi escolhido.
    if (!usuario && tipo !== "C") {
      const pode = await podeAdicionarUsuarioOperacional({
        baseUrl: BASE_URL,
        token,
        entidadeID,
        toast,
      });
      if (!pode) return;
    }

    const user = {
      nome: nome.trim(),
      email: normalizarEmail(email),
      telefone: telefone || null,
      cpf: cpf || null,
      situacao,
      data_nascimento: dataNascimento || null,
      tipo,
      id_grupo_usuario: tipo === "N" ? idGrupoUsuario : null,
    };

    try {
      if (usuario) {
        await api.put(`${BASE_URL}/api/usuarios/EditarUsuario/${usuario.id}`, user);
      } else {
        await api.post(`${BASE_URL}/api/usuarios/InserirUsuario/${entidadeID}`, user);
        toast.success("Usuário criado. Use “Alterar Senha” para enviar uma senha temporária de acesso.");
      }

      fetchUsuarios();
      handleClose();
    } catch (err) {
      setError({ message: extrairMensagemErro(err) });
    }
  };

  const handleResetarSenha = async () => {
    if (!usuario?.id) return;
    setError(null);
    setResetandoSenha(true);
    try {
      await api.post(
        `${BASE_URL}/api/usuarios/ResetarSenha/${usuario.id}`,
        { id_entidade: entidadeID },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Senha redefinida. E-mail com senha temporária enviado para ${usuario.email}.`);
    } catch (err) {
      const msg = err?.response?.data?.message || "Erro ao redefinir senha.";
      setError({ message: msg });
    } finally {
      setResetandoSenha(false);
    }
  };

  const handleExecutarVisaoColunasPadrao = async () => {
    if (!usuario?.id || !entidadeID || !token) return;
    setError(null);
    setExecutandoVisaoPadrao(true);
    try {
      await api.post(
        `${BASE_URL}/api/usuarios/ExecutarVisaoColunasPadrao/${entidadeID}/${usuario.id}`,
        { rota: "documentos" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Visão padrão de colunas aplicada com sucesso para este usuário.");
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || "Erro ao aplicar visão padrão de colunas.";
      setError({ message: msg });
    } finally {
      setExecutandoVisaoPadrao(false);
    }
  };

  return (
    <>
    <Modal
        show={show}
        onHide={handleClose}
        backdrop="static"
        size="lg"
        animation={false}
        dialogClassName="modal-dialog-top"
      >
        <Modal.Header closeButton style={{ background: "var(--surface-sunken)", borderColor: "var(--border-default)", paddingBottom: 12 }}>
          <Modal.Title style={{ fontSize: "1rem", fontWeight: 600, color: "var(--text-body)" }}>
            {usuario ? "Usuário" : "Adicionar Usuário"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ background: "var(--surface-card)", color: "var(--text-body)" }}>
          <ErrorAlert error={error} />

          <Form>
            <Row>
              <Col md={6}>
                <Form.Group controlId="formTipo" className="mt-3">
                  <Form.Label>
                    Tipo <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select value={tipo} onChange={(e) => setTipo(e.target.value)} disabled={ehUsuarioLogado}>
                    <option value="">Selecione um tipo</option>
                    <option value="A">Administrador</option>
                    <option value="N">Normal</option>
                    <option value="C">Comunicado</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              {tipo === "N" && (
                <Col md={6}>
                  <Form.Group controlId="formGrupo" className="mt-3">
                    <Form.Label>
                      Grupo de Usuário <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="um-select-add-combo">
                      <Form.Select
                        value={idGrupoUsuario}
                        onChange={(e) => setIdGrupoUsuario(e.target.value)}
                        required
                        className="um-select-add-combo-select"
                      >
                        <option value="">Selecione um grupo</option>
                        {grupos.map((gr) => (
                          <option key={gr.id} value={gr.id}>
                            {gr.descricao}
                          </option>
                        ))}
                      </Form.Select>
                      <span className="um-select-add-combo-button-wrapper">
                        <button
                          type="button"
                          className="um-select-add-combo-button"
                          onClick={abrirCadastroGrupoUsuario}
                          title="Adicionar grupo de usuario"
                          aria-label="Adicionar grupo de usuario"
                        >
                          <span className="um-select-add-action-circle" aria-hidden="true" />
                        </button>
                      </span>
                    </div>
                  </Form.Group>
                </Col>
              )}
            </Row>

            {tipo && (
              <>
                <Row>
                  <Col md={6}>
                    <Form.Group controlId="formNome" className="mt-3">
                      <Form.Label>
                        Nome <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={nome}
                        onChange={(e) => setNome(e.target.value)}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="formEmail" className="mt-3">
                      <Form.Label>
                        Email <span className="text-danger">*</span>
                      </Form.Label>
                      <Email
                        name="email"
                        value={email}
                        onChange={setEmail}
                        required
                        placeholder="usuario@empresa.com.br"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  {tipo !== "C" && (
                    <Col md={6}>
                      <Form.Group controlId="formTelefone" className="mt-3">
                        <Form.Label>Telefone</Form.Label>
                        <IMaskInput
                          mask="(00) 00000-0000"
                          value={telefone}
                          onAccept={(value) => setTelefone(value || "")}
                          className="form-control"
                          placeholder="Informe o telefone"
                        />
                      </Form.Group>
                    </Col>
                  )}
                  <Col md={6}>
                    <Form.Group controlId="formSituacao" className="mt-3">
                      <Form.Label>Situação</Form.Label>
                      <Form.Select value={situacao} onChange={(e) => setSituacao(e.target.value)}>
                        <option value="A">Ativo</option>
                        <option value="I">Inativo</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                {tipo === "C" && !usuario && (
                  <Form.Text className="text-muted d-block mt-2">
                    Usuário Comunicado não faz login. Nome e e-mail são obrigatórios para notificações.
                  </Form.Text>
                )}
              </>
            )}
          </Form>
        </Modal.Body>

        <Modal.Footer className="modal-footer-padrao">
          <div className="modal-footer-acoes-esquerda">
            {usuario?.id && tipo !== "C" && (!usuario.auth_provider || usuario.auth_provider === "LOCAL") && (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleResetarSenha}
                disabled={resetandoSenha}
                title="Gera senha temporária e envia por e-mail ao usuário"
              >
                <FaKey className="me-1" />
                {resetandoSenha ? "Enviando..." : "Alterar Senha"}
              </Button>
            )}
            {usuario?.id && (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleExecutarVisaoColunasPadrao}
                disabled={executandoVisaoPadrao}
                title="Aplica a visão padrão de colunas (grid) para este usuário"
              >
                {executandoVisaoPadrao ? "Aplicando..." : "Aplicar visão padrão de colunas"}
              </Button>
            )}
          </div>
          <div className="modal-footer-acoes-direita">
            <Cancelar onClick={handleClose} />
            <Salvar onClick={handleSave} />
          </div>
        </Modal.Footer>
    </Modal>
    {showGrupoUsuarioModal && (
      <GruposUsuarioModal
        show={showGrupoUsuarioModal}
        handleClose={handleCloseGrupoUsuarioModal}
        grupo={null}
        fetchGrupos={() => recarregarGrupos()}
        entidadeId={entidadeID}
      />
    )}
    </>
  );
};

export default UsuarioModal;
